import type { IUser } from "@features/users";
import { apiPost } from "./newApi/typed";

interface ErrorReportOptions {
    message: string;
    error?: any;
    context?: Record<string, any>;
    /**
     * ACEITO E IGNORADO. A identidade de quem sofreu o erro (`id_user`, `papel`)
     * é estampada pelo SERVIDOR a partir da sessão autenticada — o cliente não
     * tem como provar quem é, e o que ele afirmasse aqui seria PII no corpo do
     * request sem ganho nenhum.
     *
     * ponytail: mantido na assinatura para não editar os 7 call sites que já
     * passam `user`. Teto: parâmetro que não faz nada. Upgrade: remover daqui e
     * dos callers num passo separado, quando alguém encostar nesses arquivos.
     */
    user?: IUser | null;
    level?: "info" | "warn" | "error" | "fatal";
    /**
     * `referenceId` do boundary — é o número que a pessoa lê na tela de erro e
     * repete no chamado. Opcional: quem não passar continua funcionando.
     */
    reference?: string;
}

/**
 * Reporta erro de tela ao BACKEND, e só a ele.
 *
 * É a origem da aba Erros da Observabilidade (`POST /api/observability/errors`).
 *
 * O webhook do Slack morou aqui, lido de `VITE_SLACK_WEBHOOK_URL`. Variável com
 * prefixo `VITE_` é inlinada no bundle pelo Vite: o segredo ia junto no JS
 * servido a qualquer visitante, e qualquer um podia postar no canal do time.
 * Saiu em 2026-08-18; o arquivo se chamou `slack_logger.ts` até 2026-10-07.
 *
 * Duas propriedades que o teste em `errorReporter.test.ts` prende:
 * 1. nenhuma PII de usuário/paciente sai no payload;
 * 2. o front não chama `hooks.slack.com` — em nenhum ambiente, nem para `fatal`.
 */
class ErrorReporter {
    private lastSentMessages: Map<string, number> = new Map();
    private readonly THROTTLE_TIME = 1000 * 60 * 5; // 5 minutes

  // ponytail: comparação é de chave EXATA (minúscula), não substring — `cpf` é
  // redigido, `cpf_paciente` não. Substring redigiria demais (`nome_arquivo` cai
  // por causa de `nome`). Teto aceito porque a sanitização autoritativa é a do
  // servidor na ingestão (research.md: cliente é conteúdo não confiável).
  // Upgrade: se aparecer chave composta em campo, normalizar no servidor.
  private static PII_KEYS = [
    "password", "token", "authorization", "cpf", "email", "phone", "cellphone",
    "secret", "nome", "name", "telefone", "birth_date", "birthdate",
    "data_nascimento", "nascimento", "carteirinha", "rg", "cns", "endereco",
    "address", "cep", "zip", "ip_address", "user_agent", "useragent",
  ];

  private sanitizeError(error: any): any {
    if (error instanceof Error) {
      return `Message: ${error.message}`;
    }
    if (typeof error === "object" && error !== null) {
      try {
        return this.deepSanitize(error);
      } catch (e) {
        return String(error);
      }
    }
    return String(error);
  }

  private deepSanitize(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.deepSanitize(item));
    }
    if (typeof obj === "object" && obj !== null) {
      const safe: Record<string, any> = {};
      for (const key of Object.keys(obj)) {
        if ((ErrorReporter.PII_KEYS as readonly string[]).includes(key.toLowerCase())) {
          safe[key] = "[REDACTED]";
        } else {
          safe[key] = this.deepSanitize(obj[key]);
        }
      }
      return safe;
    }
    return obj;
  }

    /**
     * Limites do `POST /api/observability/errors`: acima deles o corpo é RECUSADO
     * (400), não truncado em silêncio. Truncar aqui é o que impede um stack grande
     * de virar 4xx no caminho do usuário.
     */
    private static readonly MAX_MESSAGE = 500;
    private static readonly MAX_ROUTE = 255;
    private static readonly MAX_CONTEXT_JSON = 4000;

    /**
     * Re-serializar o recorte escapa de novo cada `"` e `\`, e pode até dobrar o
     * tamanho: um corte fixo em 3000 passava do teto e o corpo tomava 400 (medido
     * no senneliquor em 2026-10-07: 4274 e 4394). Encolhe até caber.
     */
    private static truncateToLimit(asJson: string): { truncado: string } {
        let cut = asJson.slice(0, 3000);
        while (JSON.stringify({ truncado: cut }).length > ErrorReporter.MAX_CONTEXT_JSON) {
            cut = cut.slice(0, Math.floor(cut.length * 0.75));
        }
        return { truncado: cut };
    }

    /**
     * T039 — registro durável do erro (`POST /api/observability/errors`, 202).
     *
     * Três invariantes:
     * 1. **Nunca quebra nem atrasa o fluxo do usuário**: chamada sem `await` pelo
     *    caller e com a falha engolida aqui. Erro na ingestão do erro não pode
     *    virar um segundo erro — e não chama `notify` de volta, senão recursão.
     * 2. **Só `warn | error | fatal`**: o servidor recusa `info` de propósito, para
     *    a tabela (retenção de 30 dias) não virar depósito de telemetria.
     * 3. **Nada de identidade do cliente**: `id_user`, `role`, `ip`, `user_agent` e
     *    `environment` são estampados pelo SERVIDOR e ignorados se vierem daqui.
     */
    private async ingest({
        message,
        error,
        context,
        level,
        reference,
    }: {
        message: string;
        error: unknown;
        context: Record<string, unknown> | undefined;
        level: NonNullable<ErrorReportOptions["level"]>;
        reference: string | undefined;
    }): Promise<void> {
        if (level === "info") return;

        const mergedContext: Record<string, unknown> = {
            ...(context ? this.deepSanitize(context) : {}),
            ...(error ? { erro: this.sanitizeError(error) } : {}),
        };
        const asJson = JSON.stringify(mergedContext);
        const finalContext =
            asJson === "{}"
                ? undefined
                : asJson.length > ErrorReporter.MAX_CONTEXT_JSON
                    ? ErrorReporter.truncateToLimit(asJson)
                    : mergedContext;

        try {
            // Chaves do payload seguem o contrato de POST /observability/errors depois de
            // backend-senne specs/009-rename-app-error-log-pt-en (sincronizado via
            // scripts/sync-contracts.sh em src/contracts/observability/error-log.ts).
            await apiPost("/observability/errors", {
                origin: "frontend",
                severity: level,
                // `message` tem mínimo 1 no servidor: sem o fallback, um caller que
                // passe string vazia tomaria 400 em vez de registrar o erro.
                message: (message.trim() || "Erro sem mensagem").slice(
                    0,
                    ErrorReporter.MAX_MESSAGE,
                ),
                route: window.location.pathname.slice(0, ErrorReporter.MAX_ROUTE),
                ...(reference ? { reference: reference.slice(0, 64) } : {}),
                ...(finalContext ? { context: finalContext } : {}),
            });
        } catch {
            // Silêncio proposital: a ingestão não é caminho crítico.
        }
    }

    // `user` fica fora do destructuring de propósito: é o que garante que a PII do
    // caller não entra no escopo desta função e não pode escorregar para o payload.
    async notify({ message, error, context, level = "error", reference }: ErrorReportOptions) {
        // Throttle: segura o rate limit de 30/min da ingestão quando um erro de
        // render entra em laço.
        const fingerprint = `${message}-${level}`;
        const now = Date.now();
        const lastSent = this.lastSentMessages.get(fingerprint);

        if (lastSent && now - lastSent < this.THROTTLE_TIME) {
            return;
        }

        this.lastSentMessages.set(fingerprint, now);

        // Fire-and-forget: a tela não espera pelo registro do erro.
        void this.ingest({ message, error, context, level, reference });
    }
}

export const errorReporter = new ErrorReporter();
